import { memo, type ComponentType, useMemo, useState } from 'react';
import { DATA_OBJECT_ICON_NAMES, type DataObjectIconName as SharedDataObjectIconName } from '@revont/shared-types';
import {
  ArchiveBoxIcon,
  BanknotesIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  CubeIcon,
  CurrencyDollarIcon,
  IdentificationIcon,
  TagIcon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon,
  BeakerIcon,
  BoltIcon,
  BookOpenIcon,
  CalculatorIcon,
  CalendarDaysIcon,
  CameraIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FireIcon,
  FlagIcon,
  FolderIcon,
  GlobeAltIcon,
  HeartIcon,
  HomeIcon,
  KeyIcon,
  LinkIcon,
  ListBulletIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MegaphoneIcon,
  NewspaperIcon,
  PencilSquareIcon,
  PhoneIcon,
  PhotoIcon,
  PuzzlePieceIcon,
  RocketLaunchIcon,
  ServerIcon,
  ShieldCheckIcon,
  ShoppingCartIcon,
  SparklesIcon,
  StarIcon,
  TableCellsIcon,
  TrashIcon,
  TruckIcon,
  UserCircleIcon,
  WalletIcon,
  WrenchIcon,
} from '@heroicons/react/24/outline';
import { Tooltip } from './Tooltip';
import { Popover } from './Popover';
import { SearchInput } from './SearchInput';

// ---------------------------------------------------------------------------
// Icon registry - commonly useful icons for data objects
// ---------------------------------------------------------------------------

interface IconEntry {
  label: string;
  component: ComponentType<{ className?: string }>;
}

const ICON_ENTRIES: Record<SharedDataObjectIconName, IconEntry> = {
  ArchiveBoxIcon: { label: 'Archive Box', component: ArchiveBoxIcon },
  BanknotesIcon: { label: 'Banknotes', component: BanknotesIcon },
  BeakerIcon: { label: 'Beaker', component: BeakerIcon },
  BoltIcon: { label: 'Bolt', component: BoltIcon },
  BookOpenIcon: { label: 'Book Open', component: BookOpenIcon },
  BriefcaseIcon: { label: 'Briefcase', component: BriefcaseIcon },
  BuildingOffice2Icon: { label: 'Building Office', component: BuildingOffice2Icon },
  CalculatorIcon: { label: 'Calculator', component: CalculatorIcon },
  CalendarDaysIcon: { label: 'Calendar', component: CalendarDaysIcon },
  CameraIcon: { label: 'Camera', component: CameraIcon },
  ChartBarIcon: { label: 'Chart Bar', component: ChartBarIcon },
  ChatBubbleLeftRightIcon: { label: 'Chat', component: ChatBubbleLeftRightIcon },
  CheckCircleIcon: { label: 'Check Circle', component: CheckCircleIcon },
  CircleStackIcon: { label: 'Circle Stack', component: CircleStackIcon },
  ClipboardDocumentCheckIcon: { label: 'Clipboard Check', component: ClipboardDocumentCheckIcon },
  ClockIcon: { label: 'Clock', component: ClockIcon },
  Cog6ToothIcon: { label: 'Cog', component: Cog6ToothIcon },
  CreditCardIcon: { label: 'Credit Card', component: CreditCardIcon },
  CubeIcon: { label: 'Cube', component: CubeIcon },
  CurrencyDollarIcon: { label: 'Currency Dollar', component: CurrencyDollarIcon },
  DocumentTextIcon: { label: 'Document', component: DocumentTextIcon },
  EnvelopeIcon: { label: 'Envelope', component: EnvelopeIcon },
  ExclamationTriangleIcon: { label: 'Warning', component: ExclamationTriangleIcon },
  EyeIcon: { label: 'Eye', component: EyeIcon },
  FireIcon: { label: 'Fire', component: FireIcon },
  FlagIcon: { label: 'Flag', component: FlagIcon },
  FolderIcon: { label: 'Folder', component: FolderIcon },
  GlobeAltIcon: { label: 'Globe', component: GlobeAltIcon },
  HeartIcon: { label: 'Heart', component: HeartIcon },
  HomeIcon: { label: 'Home', component: HomeIcon },
  IdentificationIcon: { label: 'Identification', component: IdentificationIcon },
  KeyIcon: { label: 'Key', component: KeyIcon },
  LinkIcon: { label: 'Link', component: LinkIcon },
  ListBulletIcon: { label: 'List', component: ListBulletIcon },
  LockClosedIcon: { label: 'Lock', component: LockClosedIcon },
  MagnifyingGlassIcon: { label: 'Search', component: MagnifyingGlassIcon },
  MapPinIcon: { label: 'Map Pin', component: MapPinIcon },
  MegaphoneIcon: { label: 'Megaphone', component: MegaphoneIcon },
  NewspaperIcon: { label: 'Newspaper', component: NewspaperIcon },
  PencilSquareIcon: { label: 'Pencil Square', component: PencilSquareIcon },
  PhoneIcon: { label: 'Phone', component: PhoneIcon },
  PhotoIcon: { label: 'Photo', component: PhotoIcon },
  PuzzlePieceIcon: { label: 'Puzzle Piece', component: PuzzlePieceIcon },
  RocketLaunchIcon: { label: 'Rocket', component: RocketLaunchIcon },
  ServerIcon: { label: 'Server', component: ServerIcon },
  ShieldCheckIcon: { label: 'Shield', component: ShieldCheckIcon },
  ShoppingCartIcon: { label: 'Shopping Cart', component: ShoppingCartIcon },
  SparklesIcon: { label: 'Sparkles', component: SparklesIcon },
  StarIcon: { label: 'Star', component: StarIcon },
  TableCellsIcon: { label: 'Table', component: TableCellsIcon },
  TagIcon: { label: 'Tag', component: TagIcon },
  TicketIcon: { label: 'Ticket', component: TicketIcon },
  TrashIcon: { label: 'Trash', component: TrashIcon },
  TruckIcon: { label: 'Truck', component: TruckIcon },
  UserCircleIcon: { label: 'User Circle', component: UserCircleIcon },
  UserGroupIcon: { label: 'User Group', component: UserGroupIcon },
  UsersIcon: { label: 'Users', component: UsersIcon },
  WalletIcon: { label: 'Wallet', component: WalletIcon },
  WrenchIcon: { label: 'Wrench', component: WrenchIcon },
};

export type DataObjectIconName = SharedDataObjectIconName;

export const DATA_OBJECT_ICON_OPTIONS = [...DATA_OBJECT_ICON_NAMES];

export function isDataObjectIconName(iconName: string): iconName is DataObjectIconName {
  return Object.prototype.hasOwnProperty.call(ICON_ENTRIES, iconName);
}

// ---------------------------------------------------------------------------
// DataObjectIcon - renders a named heroicon
// ---------------------------------------------------------------------------

interface IconProps {
  className?: string;
}

const ICON_COMPONENTS: Record<string, ComponentType<IconProps>> = Object.fromEntries(
  Object.entries(ICON_ENTRIES).map(([key, entry]) => [key, entry.component])
);

export interface DataObjectIconProps {
  iconName: string;
  className?: string;
}

/**
 * Resolve an icon name to its component, or null when the name isn't in the
 * registry. Lets non-React callers (e.g. sidebar nav definitions) reuse the
 * same icon set as the picker without rendering a DataObjectIcon.
 */
export function resolveDataObjectIcon(
  iconName: string
): ComponentType<{ className?: string }> | null {
  return ICON_COMPONENTS[iconName] ?? null;
}

export function DataObjectIcon({ iconName, className = 'h-5 w-5' }: DataObjectIconProps) {
  const Icon = resolveDataObjectIcon(iconName) ?? CircleStackIcon;
  return <Icon className={className} aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// DataObjectIconPicker - flat grid (original)
// ---------------------------------------------------------------------------

export interface DataObjectIconPickerProps {
  value: string;
  onChange: (iconName: string) => void;
  label?: string;
}

export function DataObjectIconPicker({
  value,
  onChange,
  label = 'Icon',
}: DataObjectIconPickerProps) {
  return (
    <fieldset>
      <legend className="mb-2 text-xs font-medium text-text-secondary">{label}</legend>
      <div className="grid grid-cols-7 gap-2">
        {DATA_OBJECT_ICON_OPTIONS.map(iconName => {
          const entry = ICON_ENTRIES[iconName];
          const iconLabel = entry?.label ?? iconName;
          return (
            <Tooltip key={iconName} content={iconLabel} side="bottom">
              <button
                type="button"
                onClick={() => onChange(iconName)}
                className={`flex aspect-square w-full items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
                  value === iconName
                    ? 'bg-primary/10 text-primary'
                    : 'bg-surface-dark text-text-secondary hover:bg-surface-dark hover:text-text-primary'
                }`}
                aria-label={iconLabel}
                aria-pressed={value === iconName}
              >
                <DataObjectIcon iconName={iconName} className="h-5 w-5" />
              </button>
            </Tooltip>
          );
        })}
      </div>
    </fieldset>
  );
}

// ---------------------------------------------------------------------------
// DataObjectIconPickerPopover - searchable popover with full icon grid
// ---------------------------------------------------------------------------

export interface DataObjectIconPickerPopoverProps {
  value: string;
  onChange: (iconName: string) => void;
  label?: string;
}

export const DataObjectIconPickerPopover = memo(function DataObjectIconPickerPopover({
  value,
  onChange,
  label = 'Icon',
}: DataObjectIconPickerPopoverProps) {
  const [search, setSearch] = useState('');
  const selectedLabel = isDataObjectIconName(value) ? ICON_ENTRIES[value].label : value;

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return DATA_OBJECT_ICON_OPTIONS;
    const query = search.toLowerCase();
    return DATA_OBJECT_ICON_OPTIONS.filter(iconName => {
      const entry = ICON_ENTRIES[iconName];
      return iconName.toLowerCase().includes(query) || entry?.label.toLowerCase().includes(query);
    });
  }, [search]);

  return (
    <div>
      {label && <label className="block text-sm font-medium text-white/80 mb-2">{label}</label>}
      <Popover
        position="bottom-start"
        panelClassName="w-72 max-h-[22rem] overflow-hidden p-0"
        renderTrigger={({ open }) => (
          <button
            type="button"
            className={`flex items-center gap-2 rounded-lg bg-white/[0.05] px-3 py-2 text-left transition-colors hover:bg-white/[0.08] ${
              open ? 'ring-2 ring-primary/40' : ''
            }`}
          >
            <span className="flex h-6 w-6 items-center justify-center text-text-secondary">
              <DataObjectIcon iconName={value} className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1 text-sm text-text-primary">{selectedLabel}</span>
            <svg
              className={`h-4 w-4 text-text-tertiary transition-transform ${open ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        )}
      >
        {({ close }) => (
          <div className="flex max-h-[22rem] flex-col">
            <div className="p-2">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search icons..."
                size="small"
                autoFocus
              />
            </div>
            <div className="min-h-0 overflow-auto p-2">
              {filteredIcons.length === 0 ? (
                <p className="py-4 text-center text-xs text-text-tertiary">No icons found</p>
              ) : (
                <div className="grid grid-cols-6 gap-1.5">
                  {filteredIcons.map(iconName => {
                    const entry = ICON_ENTRIES[iconName];
                    const iconLabel = entry?.label ?? iconName;
                    return (
                      <Tooltip key={iconName} content={iconLabel} side="bottom">
                        <button
                          type="button"
                          onClick={() => {
                            onChange(iconName);
                            setSearch('');
                            close();
                          }}
                          className={`flex aspect-square w-full items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${
                            value === iconName
                              ? 'bg-primary/15 text-primary'
                              : 'text-text-secondary hover:bg-white/[0.06] hover:text-text-primary'
                          }`}
                          aria-label={iconLabel}
                        >
                          <DataObjectIcon iconName={iconName} className="h-5 w-5" />
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Popover>
    </div>
  );
});
